import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  getAskingPrice,
  mapValueToEbitdaFormat,
  mapValueToMoneyFormat,
} from 'utils';
import {
  AskingPriceSelect,
  Currency,
  STOCK_AT_VALUE,
  StockAtValue,
} from 'models/DealRoom';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import ResolvePopup from 'components/ResolvePopup';
import InformationcircleIcon from 'icons/Informationcircle.icon';

import './ListingPrices.styles.scss';
import './ListingPrices.styles.responsive.scss';

type ListingPricesProps = {
  className?: string;
  askingPrice: AskingPriceSelect;
  amount: number;
  amountFrom: number;
  amountTo: number;
  revenue: number;
  EBITDA: number;
  averageStockValue: number;
  stockAtValue: StockAtValue;
  currency: Currency;
};

const ListingPrices: React.FC<ListingPricesProps> = (props) => {
  const {
    className,
    amount,
    amountTo,
    amountFrom,
    askingPrice,
    averageStockValue,
    EBITDA,
    revenue,
    stockAtValue,
    currency,
  } = props;

  const { currentUser } = useContext(CurrentUserContext);

  const { t } = useTranslation();

  const hasStockValue = useMemo(
    () =>
      !!averageStockValue && stockAtValue !== STOCK_AT_VALUE.NO_STOCK_AT_VALUE,
    [averageStockValue, stockAtValue],
  );

  const showAskingPrice = useMemo(
    () =>
      getAskingPrice({
        amount,
        amountFrom,
        amountTo,
        askingPriceSelect: askingPrice,
      }),
    [amount, amountFrom, amountTo, askingPrice],
  );

  const classes = classNames('listing-prices', className);

  const infoPopup = useMemo(
    () => (
      <ResolvePopup
        onHover
        popupContent={<span>{t('pricePopupContent')}</span>}
      >
        <InformationcircleIcon />
      </ResolvePopup>
    ),
    [t],
  );

  return (
    <div className={classes}>
      {!!askingPrice && showAskingPrice && currentUser && (
        <div className="listing-prices__price">
          <span>{showAskingPrice}</span>
          <span>
            {currency
              ? t('priceGuideWithCurrency', { currency: t(currency) })
              : t('priceGuide')}
            {!averageStockValue && !EBITDA && !revenue && infoPopup}
          </span>
        </div>
      )}
      {!!revenue && (
        <div className="listing-prices__price">
          <span>{mapValueToMoneyFormat(revenue)}</span>
          <span>
            {t('revenue')}
            {!averageStockValue && !EBITDA && infoPopup}
          </span>
        </div>
      )}
      {!!EBITDA && EBITDA > 0 && (
        <div className="listing-prices__price">
          <span>{mapValueToEbitdaFormat(EBITDA)}</span>
          <span>
            {t('ebitda')}
            {!averageStockValue && infoPopup}
          </span>
        </div>
      )}
      {hasStockValue && (
        <div className="listing-prices__price">
          <span>{mapValueToMoneyFormat(averageStockValue)}</span>
          <span>
            {t(
              `stockAtValuation${
                stockAtValue === STOCK_AT_VALUE.INCLUDE_STOCK_AT_VALUE
                  ? 'Included'
                  : 'Excluded'
              }`,
            )}
            {infoPopup}
          </span>
        </div>
      )}
    </div>
  );
};

export default ListingPrices;
